import { useEffect, useRef, useState } from 'react';
import { Modal } from './ui';

/**
 * Camera barcode scanning. Uses the browser's BarcodeDetector where available
 * (Chrome/Android), otherwise ZXing (works on iPhone Safari). Manual entry and
 * USB/Bluetooth scanners always remain available, so this is optional.
 */
export function CameraScanner({ title, onResult, onClose }: { title: string; onResult: (code: string) => void; onClose: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const resultRef = useRef(onResult);
  resultRef.current = onResult;

  useEffect(() => {
    let stopped = false;
    let stream: MediaStream | null = null;
    let zxingControls: { stop: () => void } | null = null;
    let timer: number | undefined;

    const finish = (text: string) => {
      if (stopped) return;
      stopped = true;
      if ('vibrate' in navigator) navigator.vibrate?.(80);
      resultRef.current(text);
    };

    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('This browser cannot use the camera here. Type the number or use a scanner.');
        return;
      }
      const BD = (window as any).BarcodeDetector;
      try {
        if (BD) {
          stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
          const v = videoRef.current!;
          v.srcObject = stream;
          await v.play();
          const detector = new BD();
          const tick = async () => {
            if (stopped) return;
            try {
              const codes = await detector.detect(v);
              if (codes[0]?.rawValue) return finish(codes[0].rawValue);
            } catch {
              /* frame not ready */
            }
            timer = window.setTimeout(tick, 150);
          };
          tick();
        } else {
          const { BrowserMultiFormatReader } = await import('@zxing/browser');
          const reader = new BrowserMultiFormatReader();
          zxingControls = await reader.decodeFromConstraints({ video: { facingMode: 'environment' } }, videoRef.current!, (result) => {
            if (result) finish(result.getText());
          });
        }
      } catch (e) {
        setError(`Camera unavailable: ${(e as Error).message}. Type the number or use a scanner instead.`);
      }
    })();

    return () => {
      stopped = true;
      clearTimeout(timer);
      zxingControls?.stop();
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return (
    <Modal title={title} onClose={onClose}>
      {error ? <div className="alert alert-warn">{error}</div> : <p className="muted">Point the camera at the barcode.</p>}
      <video ref={videoRef} className="camera-video" playsInline muted />
    </Modal>
  );
}
